import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import { toast } from "../../../components/Toast/toast";
import { btn } from "../../../components/Button/buttonStyles";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "—";

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const COVERAGE_CLS = {
  "Fully covered": "text-green-600 font-medium",
  "Partially covered": "text-amber-600 font-medium",
  "Not covered": "text-danger-500 font-medium",
};

const Row = ({ label, value, children }) => (
  <div className="flex border-b last:border-b-0 text-sm">
    <div className="w-1/3 bg-gray-50 px-4 py-3 font-medium text-gray-700">
      {label}
    </div>
    <div className="w-2/3 px-4 py-3 text-gray-900">
      {children ?? value ?? "—"}
    </div>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-white shadow rounded-lg overflow-hidden">
    {title && (
      <div className="px-5 py-3 border-b border-gray-100">
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      </div>
    )}
    <div>{children}</div>
  </div>
);

const VendorPaymentDetailPage = () => {
  const { id } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    api
      .get(`/vendor-payments/${id}`, { withCredentials: true })
      .then((res) => setPayment(res.data.data))
      .catch(() => setError("Failed to fetch payment details"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading)
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  if (error)
    return <p className="text-danger-600 text-sm">{error}</p>;
  if (!payment)
    return <p className="text-sm text-gray-500">Payment not found.</p>;

  const pm = payment.paymentMethod;
  const pmType = pm?.payment_method?.replace(/_/g, " ") || "—";

  let pmName = "—";
  let pmDetails = "—";
  if (pm?.bank_transfer_detail) {
    const bt = pm.bank_transfer_detail;
    pmName = bt.payment_method_name || bt.bank_name || "—";
    const parts = [];
    if (bt.bank_name) parts.push(`Bank name: ${bt.bank_name}`);
    if (bt.bank_address) parts.push(`Bank address: ${bt.bank_address}`);
    if (bt.country) parts.push(`Country: ${bt.country}`);
    pmDetails = parts.join("\n") || "—";
  } else if (pm?.email_payment_detail) {
    const ep = pm.email_payment_detail;
    pmName = ep.email || "—";
    pmDetails = ep.email || "—";
  } else if (pm?.other_payment_detail) {
    pmName = pm.other_payment_detail.payment_method_name || "—";
  }

  const links = (payment.invoiceLinks || []).filter((l) => l.invoice);
  const legalEntity = payment.vendor?.legal_entity || "—";
  const coverageCls =
    COVERAGE_CLS[payment.coverage_by_invoices] ?? "text-gray-600";

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BackButton />
          <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
            Payment {payment.payment_code}
          </h1>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => toast.error("Download as PDF coming soon.")}
            className={btn("primary", "sm")}
          >
            Download as PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard>
          <Row label="Total amount">
            <strong>{fmtNum(payment.amount)}</strong>
          </Row>
          <Row label="Currency" value={payment.currency || "—"} />
          <Row label="Paid at" value={fmtDate(payment.paid_at)} />

          <div className="px-4 py-3 border-b bg-gray-50">
            <p className="text-sm font-semibold text-gray-800 tracking-wide">
              Invoice coverage
            </p>
          </div>
          <Row label="Invoice coverage">
            <span className={coverageCls}>
              {payment.coverage_by_invoices || "Not covered"}
            </span>
          </Row>
          <Row label="Linked amount" value={fmtNum(payment.linked_amount)} />
          <Row label="Unlinked amount" value={fmtNum(payment.unlinked_amount)} />

          <div className="px-4 py-3 border-b bg-gray-50">
            <p className="text-sm font-semibold text-gray-800 tracking-wide">
              Other details
            </p>
          </div>
          <Row label="Legal entity" value={legalEntity} />
          <Row label="Note" value={payment.note || "—"} />
          <Row label="Created at" value={fmtDate(payment.createdAt)} />
        </SectionCard>

        <SectionCard title="Payment Method">
          <Row label="Name" value={pmName} />
          <Row label="Type">
            <span className="capitalize">{pmType}</span>
          </Row>
          <Row label="Details">
            <span className="whitespace-pre-line">{pmDetails}</span>
          </Row>
        </SectionCard>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">
            Linked Invoices
            <span className="ml-2 text-gray-500 font-medium">
              ({links.length})
            </span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse min-w-[900px]">
            <thead className="bg-white border-b">
              <tr className="text-[10px] tracking-widest text-gray-500 uppercase">
                {[
                  "Code",
                  "Payment coverage",
                  "Total after write off",
                  "Amount covered by linked payments",
                  "Amount covered by this payment",
                  "Amount due",
                  "Issued at",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {links.length ? (
                links.map((link, i) => {
                  const inv = link.invoice;
                  const coverage =
                    Number(inv.due_amount) <= 0 ? "Covered" : Number(inv.paid_amount) > 0 ? "Partially covered" : "Not covered";
                  return (
                    <tr key={link.id} className={`${i % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-brand-50 transition-colors`}>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                        <Link
                          to={`/vendor/invoice/${inv.id}`}
                          className="text-brand-600 hover:text-brand-800 underline font-medium"
                        >
                          {inv.invoice_code}
                        </Link>
                      </td>
                      <td className={`px-3 py-2 text-xs text-center ${COVERAGE_CLS[coverage] ?? "text-green-600 font-medium"}`}>
                        {coverage}
                      </td>
                      <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                        {fmtNum(Number(inv.total || 0) - Number(inv.write_off_amount || 0))}
                      </td>
                      <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                        {fmtNum(inv.paid_amount)}
                      </td>
                      <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900 font-semibold">
                        {fmtNum(link.amount)}
                      </td>
                      <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                        {fmtNum(inv.due_amount)}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500 text-center">
                        {fmtDate(inv.issued_at)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="py-10 text-center text-gray-400 text-xs"
                  >
                    No linked invoices.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SectionCard title="Messages">
        <div className="px-5 py-4 space-y-3">
          <textarea
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write a message..."
            className="w-full border border-gray-300 rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <div className="flex justify-end">
            <button
              disabled={!message.trim()}
              className={btn("primary", "sm")}
            >
              Send
            </button>
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

export default VendorPaymentDetailPage;
